<template>
    <div class="relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <h1 class="stage-page-title">
                        <span>Finanzen</span>
                        <span class="mx-1.5 text-gray-400" aria-hidden="true">·</span>
                        <span>Dokumente</span>
                    </h1>
                </div>
                <div class="stage-page-header-actions">
                    <router-link
                        class="btn-stage-icon-plain"
                        to="/workspace/finance"
                        title="Zurück zur Finanzübersicht"
                        aria-label="Zurück zur Finanzübersicht"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-left"></svg-vue>
                    </router-link>
                    <button
                        type="button"
                        class="btn-stage-icon-plain stage-filter-icon-button"
                        :class="{'text-red-500': hasActiveFilters}"
                        title="Filter"
                        aria-label="Filter"
                        @click="filterSidebarOpen = !filterSidebarOpen"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                        <span v-if="hasActiveFilters" aria-hidden="true" class="stage-filter-active-dot"></span>
                    </button>
                    <button type="button" class="btn-stage-icon-plain" title="Neu laden" aria-label="Neu laden" @click="fetchDocuments">
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow sm:rounded relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <aside
                    :aria-hidden="!filterSidebarOpen"
                    class="stage-filter-sidebar stage-filter-panel w-80 overflow-auto z-40"
                    :class="filterSidebarOpen ? 'stage-filter-sidebar-open' : 'stage-filter-sidebar-closed'"
                >
                    <div class="stage-filter-body">
                        <div class="stage-filter-title-row">
                            <svg-vue class="stage-filter-title-icon h-5 w-5 text-gray-600 mr-3" icon="font-awesome.regular.sliders"></svg-vue>
                            <div class="stage-filter-title">Filter &amp; Sortierung</div>
                        </div>

                        <div class="stage-filter-label">Suche</div>
                        <div class="mb-5 relative">
                            <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                            </div>
                            <input
                                v-model="filters.search"
                                class="stage-filter-input form-input block pl-10 text-sm"
                                placeholder="Datei, Nummer oder Unternehmen"
                                @keyup.enter="applyFilters"
                            >
                        </div>

                        <div class="stage-filter-label">Verknüpfung</div>
                        <select v-model="filters.linkState" class="form-select block w-full text-sm mb-5" @change="applyFilters">
                            <option value="all">Alle Dokumente</option>
                            <option value="linked">Mit Finanzeintrag</option>
                            <option value="unlinked">Ohne Verknüpfung</option>
                        </select>

                        <template v-if="labels.length > 0">
                            <div class="stage-filter-label">Labels</div>
                            <div class="mb-5 max-h-52 overflow-auto rounded border border-bordergray-100 bg-white px-3 py-2">
                                <label v-for="label in labels" :key="'filter-label-' + label.id" class="flex cursor-pointer items-center py-1">
                                    <input v-model="selectedLabelIds" :value="Number(label.id)" type="checkbox" class="form-checkbox mr-2 h-4 w-4 text-gray-600" @change="applyFilters">
                                    <span class="finance-document-label-dot mr-2" :style="{backgroundColor: label.color || 'var(--ui-text-subtle)'}"></span>
                                    <span class="min-w-0 flex-1 truncate text-sm text-gray-700">{{ label.name }}</span>
                                    <span class="ml-2 text-sm text-gray-500">{{ label.files_count || 0 }}</span>
                                </label>
                            </div>
                        </template>

                        <div class="stage-filter-label">Sortieren nach</div>
                        <div class="stage-filter-row mb-5">
                            <button class="stage-filter-sort-btn text-sm leading-5 font-medium" type="button" @click="toggleSort">
                                <svg-vue class="h-4 w-4 text-gray-400" icon="font-awesome.regular.arrow-down-arrow-up"></svg-vue>
                            </button>
                            <div class="stage-filter-sort-select-wrap">
                                <select v-model="sort.column" class="stage-filter-sort-select block form-select text-sm leading-5 font-medium" @change="applyFilters">
                                    <option value="updated_at">Letztes Update</option>
                                    <option value="document_date">Datum des Dokuments</option>
                                    <option value="document_category">Kategorie</option>
                                    <option value="created_at">Upload-Datum</option>
                                </select>
                                <span class="stage-filter-select-icon">
                                    <svg-vue class="h-[0.875rem] w-[0.875rem] text-gray-400" icon="font-awesome.regular.angle-down"></svg-vue>
                                </span>
                            </div>
                        </div>

                        <button v-if="hasActiveFilters" type="button" class="text-sm text-gray-500 hover:text-gray-700" @click="resetFilters">
                            Auswahl zurücksetzen
                        </button>
                    </div>
                </aside>

                <div class="border-b border-ultralight">
                    <div class="stage-header-surface">
                        <div class="flex min-h-[52px] items-stretch justify-between">
                            <div class="inline-flex items-stretch">
                                <div class="inline-flex items-center px-4 border-r border-ultralight">
                                    <button
                                        type="button"
                                        class="finance-document-view-btn"
                                        :class="{'finance-document-view-btn--active': viewMode === 'list'}"
                                        title="Listenansicht"
                                        aria-label="Listenansicht"
                                        :aria-pressed="viewMode === 'list' ? 'true' : 'false'"
                                        @click="viewMode = 'list'"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.bars"></svg-vue>
                                    </button>
                                    <button
                                        type="button"
                                        class="finance-document-view-btn ml-2"
                                        :class="{'finance-document-view-btn--active': viewMode === 'grid'}"
                                        title="Kachelansicht"
                                        aria-label="Kachelansicht"
                                        :aria-pressed="viewMode === 'grid' ? 'true' : 'false'"
                                        @click="viewMode = 'grid'"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.grid-2"></svg-vue>
                                    </button>
                                </div>

                                <div
                                    v-on-clickaway="closeCategoryMenu"
                                    class="finance-document-category-select relative flex min-w-0 flex-shrink-0 items-stretch border-r border-ultralight"
                                >
                                    <button
                                        :aria-expanded="categoryMenuOpen ? 'true' : 'false'"
                                        aria-haspopup="listbox"
                                        class="finance-document-category-trigger relative flex h-full min-w-[18rem] max-w-[26rem] items-center bg-transparent py-0 pl-5 pr-10 text-left text-sm font-medium leading-5 text-gray-800 focus:outline-none"
                                        type="button"
                                        @click="categoryMenuOpen = !categoryMenuOpen"
                                    >
                                        <span class="min-w-0 flex-1 truncate">{{ currentCategoryLabel }}</span>
                                        <svg-vue
                                            class="absolute right-4 h-[0.875rem] w-[0.875rem] text-gray-900"
                                            icon="font-awesome.regular.angle-down"
                                        ></svg-vue>
                                    </button>

                                    <div
                                        v-show="categoryMenuOpen"
                                        class="finance-document-category-menu absolute left-0 top-full mt-2 min-w-full overflow-hidden rounded border border-bordergray-100 bg-white shadow-lg"
                                        role="listbox"
                                    >
                                        <button
                                            v-for="category in categoryOptions"
                                            :key="category.value || 'all'"
                                            :aria-selected="filters.documentCategory === category.value ? 'true' : 'false'"
                                            :class="filters.documentCategory === category.value ? 'bg-gray-50 font-semibold text-gray-900' : 'font-normal text-gray-700 hover:bg-gray-50'"
                                            class="flex w-full min-w-0 items-center px-4 py-2 text-left text-sm"
                                            role="option"
                                            type="button"
                                            @click="selectCategory(category.value)"
                                        >
                                            <span class="min-w-0 flex-1 truncate">{{ category.label }}</span>
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <div class="ml-auto inline-flex items-stretch">
                                <div
                                    v-if="canManageLabels"
                                    v-on-clickaway="closeLabelManager"
                                    class="relative inline-flex items-stretch border-l border-r border-ultralight"
                                >
                                    <button
                                        type="button"
                                        class="inline-flex h-full w-12 items-center justify-center text-gray-700 focus:outline-none"
                                        aria-label="Labels verwalten"
                                        title="Labels verwalten"
                                        @click.stop="toggleLabelManager"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.label"></svg-vue>
                                    </button>
                                    <div v-show="labelManagerOpen" class="finance-document-label-manager absolute right-0 top-full mt-2 w-64 overflow-hidden rounded-sm border border-bordergray-100 bg-white shadow-[0_8px_20px_rgba(15,23,42,0.08)]">
                                        <div class="border-b border-bordergray-100">
                                            <category-create-input
                                                ref="labelManagerInput"
                                                v-model:color="labelDraft.color"
                                                v-model:name="labelDraft.name"
                                                :maxlength="120"
                                                placeholder="Farbe und Label"
                                                @submit="createLabel"
                                            />
                                        </div>
                                        <div class="max-h-56 overflow-auto">
                                            <div v-for="label in labels" :key="'manager-label-' + label.id" class="finance-document-label-manager-row flex items-center border-b border-bordergray-100 last:border-b-0">
                                                <div class="finance-document-label-manager-row-content inline-flex min-w-0 flex-1 items-center">
                                                    <span class="finance-document-label-manager-row-dot rounded-full flex-shrink-0" :style="{backgroundColor: label.color || 'var(--ui-text-subtle)'}"></span>
                                                    <span class="truncate text-sm text-gray-700">{{ label.name }}</span>
                                                    <span class="ml-2 text-xs text-gray-500">({{ label.files_count || 0 }})</span>
                                                </div>
                                                <div class="finance-document-label-manager-row-actions">
                                                    <button
                                                        v-if="canDeleteLabel(label)"
                                                        type="button"
                                                        class="finance-document-label-delete-btn inline-flex items-center justify-center text-gray-500 hover:text-gray-700 focus:outline-none"
                                                        aria-label="Label löschen"
                                                        @click.stop="deleteLabel(label)"
                                                    >
                                                        <svg-vue class="h-4 w-4" icon="font-awesome.trash-alt-regular"></svg-vue>
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div class="w-32 border-l border-ultralight">
                                    <button
                                        type="button"
                                        class="btn-stage-primary h-full w-full border-0"
                                        style="border-radius: 0;"
                                        @click="openUploadDialog"
                                    >
                                        Hochladen
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <div class="h-full overflow-auto">
                    <div v-if="!loading && documents.length === 0" class="stage-empty-state">
                        <div class="stage-empty-state-inner">
                            <span class="stage-empty-state-icon-wrap">
                                <svg-vue class="stage-empty-state-icon" icon="font-awesome.regular.magnifying-glass"></svg-vue>
                            </span>
                            <h3 class="stage-empty-state-title">Keine Finanzdokumente gefunden</h3>
                        </div>
                    </div>

                    <table v-else-if="viewMode === 'list'" class="table-listview table-listview--zebra finance-document-list-table">
                        <thead>
                        <tr>
                            <th class="table-listview-l-th finance-document-list-sticky-th">Dokument</th>
                            <th class="table-listview-l-th finance-document-list-sticky-th w-52">Letzte Änderung</th>
                            <th class="table-listview-l-th finance-document-list-sticky-th w-52">Dok.-Nummer</th>
                            <th class="table-listview-l-th finance-document-list-sticky-th w-40">Datum</th>
                            <th class="table-listview-l-th finance-document-list-sticky-th w-56">Verknüpfungen</th>
                            <th class="table-listview-l-th table-listview-l-th-right finance-document-list-sticky-th w-32">Aktionen</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr v-for="document in documents" :key="document.id">
                            <td class="table-listview-l-td">
                                <div class="flex items-center min-w-0">
                                    <span class="finance-document-file-icon flex-shrink-0">
                                        <svg-vue class="h-7 w-7" :icon="fileIcon(document)"></svg-vue>
                                    </span>
                                    <div class="ml-3 min-w-0">
                                        <div class="flex min-w-0 items-center text-sm font-medium text-gray-700">
                                            <span class="truncate">{{ fileName(document) }}</span>
                                            <span v-if="documentLabel(document)" class="finance-document-label-dot ml-2" :style="{backgroundColor: documentLabel(document).color || 'var(--ui-text-subtle)'}"></span>
                                        </div>
                                        <div class="text-xs text-gray-400 truncate">{{ document.document_category_label || '-' }}</div>
                                    </div>
                                </div>
                            </td>
                            <td class="table-listview-l-td">
                                <div class="flex min-w-0 items-center">
                                    <img
                                        v-if="updaterAvatar(document)"
                                        :src="updaterAvatar(document)"
                                        :alt="updaterName(document)"
                                        :title="updaterName(document)"
                                        class="h-6 w-6 flex-shrink-0 rounded-full"
                                    >
                                    <div class="min-w-0" :class="{'ml-2': updaterAvatar(document)}">
                                        <div class="truncate text-sm text-gray-600">{{ updaterName(document) }}</div>
                                        <div v-if="document.updated_at" class="truncate text-xs text-gray-400">{{ $momentFormatDateTimeAgo(document.updated_at) }}</div>
                                    </div>
                                </div>
                            </td>
                            <td class="table-listview-l-td">
                                <identifier-badge
                                    v-if="document.document_number"
                                    :value="document.document_number"
                                    title="Individuelle Dokumentennummer"
                                ></identifier-badge>
                                <span v-else class="text-sm text-gray-600">-</span>
                            </td>
                            <td class="table-listview-l-td text-sm text-gray-600">{{ formatDate(document.document_date) }}</td>
                            <td
                                class="table-listview-l-td text-sm"
                                :class="Number(document.finance_entries_count || 0) === 0 ? 'text-gray-400' : 'text-gray-600'"
                            >
                                {{ linkLabel(document) }}
                            </td>
                            <td class="table-listview-l-td text-right whitespace-nowrap">
                                <button v-if="isImageDocument(document)" type="button" class="inline-flex" title="Vorschau" @click.stop="openDocumentPreview(document)">
                                    <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.eye"></svg-vue>
                                </button>
                                <a :href="downloadUrl(document)" class="inline-flex ml-3 text-gray-700" title="Herunterladen">
                                    <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                                </a>
                                <div v-if="document.can_write && labels.length > 0" v-on-clickaway="closeDocumentLabelMenu" class="relative ml-3 inline-flex">
                                    <button type="button" class="inline-flex text-gray-700" title="Label zuweisen" @click.stop="toggleDocumentLabelMenu(document)">
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.label"></svg-vue>
                                    </button>
                                    <div v-show="isDocumentLabelMenuOpen(document)" class="finance-document-label-menu absolute right-0 top-full mt-2 w-52 overflow-hidden rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg">
                                        <button type="button" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" @click.stop="assignLabel(document, null)">Kein Label</button>
                                        <button v-for="label in labels" :key="'list-label-' + document.id + '-' + label.id" type="button" class="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" @click.stop="assignLabel(document, label)">
                                            <span class="finance-document-label-dot mr-2" :style="{backgroundColor: label.color || 'var(--ui-text-subtle)'}"></span>
                                            <span class="truncate">{{ label.name }}</span>
                                        </button>
                                    </div>
                                </div>
                                <div v-if="document.can_write" v-on-clickaway="closeDocumentActionMenu" class="relative ml-3 inline-flex">
                                    <button type="button" class="inline-flex" title="Aktionen" aria-label="Aktionen" @click.stop="toggleDocumentActionMenu(document)">
                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                    </button>
                                    <transition
                                        duration="100"
                                        enter-active-class="transition ease-out duration-100"
                                        enter-class="transform opacity-0 scale-95"
                                        enter-to-class="transform opacity-100 scale-100"
                                        leave-active-class="transition ease-in duration-75"
                                        leave-class="transform opacity-100 scale-100"
                                        leave-to-class="transform opacity-0 scale-95"
                                    >
                                        <div v-show="isDocumentActionMenuOpen(document)" class="finance-document-action-menu origin-top-right absolute right-0 top-full mt-2 w-44 rounded shadow-lg">
                                            <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100" role="menu">
                                                <button type="button" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" @click.stop="openEditFromActionMenu(document)">Editieren</button>
                                                <button v-if="document.can_delete" type="button" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150" @click.stop="openDeleteDocumentDialog(document)">Löschen</button>
                                            </div>
                                        </div>
                                    </transition>
                                </div>
                            </td>
                        </tr>
                        </tbody>
                    </table>

                    <div v-else class="p-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 finance-document-tile-grid">
                        <article
                            v-for="document in documents"
                            :key="document.id"
                            class="finance-document-card relative border border-bordergray-100 rounded bg-white"
                            :style="documentLabel(document) ? {borderLeftColor: documentLabel(document).color, borderLeftWidth: 'calc(0.28rem + 1px)'} : {}"
                        >
                            <div class="px-6 py-6 text-center finance-document-tile-header relative">
                                <div v-if="document.can_write && labels.length > 0" v-on-clickaway="closeDocumentLabelMenu" class="absolute left-3 top-3 inline-flex">
                                    <button
                                        type="button"
                                        class="inline-flex items-center justify-center text-gray-700 focus:outline-none"
                                        title="Label zuweisen"
                                        @click.stop="toggleDocumentLabelMenu(document)"
                                    >
                                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.label"></svg-vue>
                                    </button>
                                    <div
                                        v-show="isDocumentLabelMenuOpen(document)"
                                        class="finance-document-label-menu absolute left-0 top-full mt-2 w-52 overflow-hidden rounded border border-bordergray-100 bg-white stage-dropdown-menu shadow-lg"
                                    >
                                        <button type="button" class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100" @click.stop="assignLabel(document, null)">Kein Label</button>
                                        <button
                                            v-for="label in labels"
                                            :key="'grid-label-' + document.id + '-' + label.id"
                                            type="button"
                                            class="flex w-full items-center px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100"
                                            @click.stop="assignLabel(document, label)"
                                        >
                                            <span class="finance-document-label-dot mr-2" :style="{backgroundColor: label.color || 'var(--ui-text-subtle)'}"></span>
                                            <span class="truncate">{{ label.name }}</span>
                                        </button>
                                    </div>
                                </div>
                                <svg-vue class="h-14 w-14 mx-auto mb-4 finance-document-file-icon" :icon="fileIcon(document)"></svg-vue>
                                <div class="flex items-center justify-center w-full text-sm text-gray-800 px-2 min-w-0">
                                    <span class="inline-flex items-center min-w-0 max-w-full">
                                        <span class="finance-document-title-text">{{ fileName(document) }}</span>
                                        <app-tooltip
                                            v-if="document.description"
                                            :text="shortDescriptionTooltip(document.description)"
                                            class="ml-2 flex-shrink-0 inline-flex"
                                            width-class="break-words"
                                            min-width="8rem"
                                            max-width="200px"
                                            :fit-content="true"
                                            :bubble-z-index="700"
                                        >
                                            <svg-vue class="h-4 w-4 text-gray-500 flex-shrink-0" icon="font-awesome.regular.circle-info"></svg-vue>
                                        </app-tooltip>
                                    </span>
                                </div>
                                <div class="mt-1 text-xs text-gray-500 text-center">{{ tileMeta(document) }}</div>
                                <identifier-badge
                                    v-if="document.document_number"
                                    :value="document.document_number"
                                    class="mt-2"
                                    title="Individuelle Dokumentennummer"
                                ></identifier-badge>
                            </div>
                            <div class="h-12 px-4 border-t border-bordergray-100 flex items-center justify-between text-xs text-gray-500">
                                <div class="inline-flex items-center min-w-0">
                                    <img
                                        v-if="updaterAvatar(document)"
                                        :src="updaterAvatar(document)"
                                        :alt="updaterName(document)"
                                        :title="updaterName(document)"
                                        class="h-6 w-6 rounded-full flex-shrink-0 mr-2"
                                    >
                                    <span v-if="document.updated_at" class="truncate">{{ $momentFormatDateTimeAgo(document.updated_at) }}</span>
                                </div>
                                <div class="inline-flex items-center flex-shrink-0">
                                    <button v-if="isImageDocument(document)" type="button" class="inline-flex" title="Vorschau" @click.stop="openDocumentPreview(document)">
                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.eye"></svg-vue>
                                    </button>
                                    <a :href="downloadUrl(document)" class="inline-flex ml-3" title="Herunterladen">
                                        <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                                    </a>
                                    <div v-if="document.can_write" v-on-clickaway="closeDocumentActionMenu" class="relative inline-flex ml-3">
                                        <button
                                            type="button"
                                            class="inline-flex"
                                            title="Aktionen"
                                            aria-label="Aktionen"
                                            @click.stop="toggleDocumentActionMenu(document)"
                                        >
                                            <svg-vue class="h-4 w-4 text-gray-700" icon="font-awesome.regular.ellipsis-vertical"></svg-vue>
                                        </button>
                                        <transition
                                            duration="100"
                                            enter-active-class="transition ease-out duration-100"
                                            enter-class="transform opacity-0 scale-95"
                                            enter-to-class="transform opacity-100 scale-100"
                                            leave-active-class="transition ease-in duration-75"
                                            leave-class="transform opacity-100 scale-100"
                                            leave-to-class="transform opacity-0 scale-95"
                                        >
                                            <div v-show="isDocumentActionMenuOpen(document)" class="finance-document-action-menu origin-top-right absolute right-0 bottom-full mb-2 w-44 rounded shadow-lg">
                                                <div class="stage-dropdown-menu rounded bg-white shadow-xs border border-bordergray-100" role="menu">
                                                    <button
                                                        type="button"
                                                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                        @click.stop="openEditFromActionMenu(document)"
                                                    >
                                                        Editieren
                                                    </button>
                                                    <button
                                                        v-if="document.can_delete"
                                                        type="button"
                                                        class="block w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 transition ease-in-out duration-150"
                                                        @click.stop="openDeleteDocumentDialog(document)"
                                                    >
                                                        Löschen
                                                    </button>
                                                </div>
                                            </div>
                                        </transition>
                                    </div>
                                </div>
                            </div>
                        </article>
                    </div>
                    </div>
                </div>

                <list-footer-pagination
                    :start="paginationStart"
                    :end="paginationEnd"
                    :total="pagination.total"
                    :current-page="pagination.currentPage"
                    :total-pages="pagination.totalPages"
                    @prev="changePage(pagination.currentPage - 1)"
                    @next="changePage(pagination.currentPage + 1)"
                />
            </div>
        </div>

        <confirm-dialog
            :open="deleteDocumentDialogOpen"
            title="Finanzdokument löschen"
            text="Soll das Dokument wirklich gelöscht werden? Durch das Löschen werden die Dateiverknüpfungen aus den Finanzeinträgen entfernt."
            confirm-label="Löschen"
            close-label="Abbrechen"
            @close="closeDeleteDocumentDialog"
            @confirm="deleteDocument"
        />

        <div v-show="previewModal.open" class="app-dialog-root fixed inset-0 overflow-y-auto" style="z-index: 1200;" @click.self="closeDocumentPreview">
            <div class="flex items-center justify-center min-h-screen px-4 py-8">
                <div class="app-dialog-overlay fixed inset-0 transition-opacity" style="z-index: 1190;"><div class="absolute inset-0 bg-black opacity-80"></div></div>
                <div class="relative max-w-6xl w-full flex items-center justify-center" style="z-index: 1200;">
                    <div class="relative inline-block max-w-full">
                        <button type="button" class="absolute -top-9 right-0 z-10 inline-flex items-center justify-center w-8 h-8 p-0 bg-transparent border-0 text-white leading-none focus:outline-none" @click="closeDocumentPreview">
                            <svg viewBox="0 0 24 24" fill="none" class="h-6 w-6">
                                <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <img :src="previewModal.url" :alt="previewModal.title || 'Dateivorschau'" class="block max-h-[85vh] w-auto max-w-full object-contain">
                    </div>
                </div>
            </div>
        </div>

        <input ref="uploadInput" type="file" hidden :accept="acceptedExtensions" @change="selectUploadFile">
        <app-dialog
            :open="uploadDialog.open"
            :title="uploadDialog.mode === 'edit' ? 'Finanzdokument bearbeiten' : 'Finanzdokument hochladen'"
            size="md"
            :busy="uploading"
            @close="closeUploadDialog"
        >
            <div class="grid grid-cols-1 gap-5 md:grid-cols-2">
                <div v-if="uploadDialog.mode === 'edit'" class="md:col-span-2">
                    <label class="finance-document-field-label">Dateiname</label>
                    <input v-model="uploadDialog.fileName" type="text" maxlength="255" class="form-input block w-full" required>
                    <div class="mt-2 text-xs text-gray-400">
                        Dateiname beim Upload am {{ editFileUploadedAtDate }} um {{ editFileUploadedAtTime }}: {{ uploadDialog.uploadedFileName || '-' }}
                    </div>
                    <div class="stage-file-upload-control mt-4">
                        <button type="button" class="btn btn-white stage-file-upload-control__button" data-dialog-autofocus @click="$refs.uploadInput.click()">
                            <span>Datei ersetzen</span>
                        </button>
                        <span v-if="uploadDialog.file" class="stage-file-upload-control__filename truncate">
                            Ausgewählt: {{ uploadDialog.file.name }}
                        </span>
                        <span v-else class="stage-file-upload-control__filename truncate">
                            Keine Ersatzdatei ausgewählt
                        </span>
                    </div>
                    <div class="mt-2 text-xs text-gray-400">
                        Verknüpfungen in den Finanzeinträgen bleiben beim Ersetzen der Datei erhalten.
                    </div>
                </div>
                <div v-else class="md:col-span-2">
                    <label class="finance-document-field-label">Datei</label>
                    <div class="stage-file-upload-control">
                        <button type="button" class="btn btn-white stage-file-upload-control__button" data-dialog-autofocus @click="$refs.uploadInput.click()">
                            <span>Auswählen</span>
                        </button>
                        <span class="stage-file-upload-control__filename truncate">
                            {{ selectedDialogFileLabel }}
                        </span>
                    </div>
                    <div class="mt-2 text-xs text-gray-400">PDF, XML, JPG, PNG, TIFF, DOCX, XLSX oder CSV · maximal 50 MB</div>
                </div>
                <div>
                    <label class="finance-document-field-label">Kategorie</label>
                    <select v-model="uploadDialog.documentCategory" class="form-select block w-full">
                        <option v-for="category in documentCategories" :key="category.value" :value="category.value">{{ category.label }}</option>
                    </select>
                </div>
                <div>
                    <label class="finance-document-field-label">Datum des Dokuments</label>
                    <input v-model="uploadDialog.documentDate" type="date" class="form-input block w-full">
                </div>
                <div class="md:col-span-2">
                    <label class="finance-document-field-label">Individuelle Dokumentennummer</label>
                    <input v-model="uploadDialog.documentNumber" type="text" maxlength="120" class="form-input block w-full" placeholder="z. B. RE-2026-0001">
                </div>
                <div class="md:col-span-2">
                    <label class="finance-document-field-label">Beschreibung</label>
                    <textarea v-model="uploadDialog.description" rows="3" maxlength="2000" class="form-textarea block w-full"></textarea>
                </div>
            </div>

            <template #footer>
                <app-dialog-action
                    :label="uploadDialog.mode === 'edit' ? 'Speichern' : 'Hochladen'"
                    loading-label="Wird gespeichert …"
                    :disabled="dialogSubmitDisabled"
                    :loading="uploading"
                    @click="submitDocumentDialog"
                />
            </template>
        </app-dialog>
    </div>
</template>

<script>
import {getAuthToken} from '@/utils/auth-token';
import {
    buildFilePreviewUrl,
    buildShortDescriptionTooltip,
    isImageFileExtension,
    resolveFileIcon,
    resolveFileUserAvatar,
} from '@/utils/workspace-file-manager';
import {resolveAccessState} from '@/utils/access-state';
import CategoryCreateInput from '@/components/forms/category-create-input.vue';
import AppDialog from '@/components/modals/app-dialog.vue';
import AppDialogAction from '@/components/modals/app-dialog-action.vue';
import ConfirmDialog from '@/components/modals/confirm-dialog.vue';
import {UI_COLORS} from '@/utils/ui-colors';

function emptyUploadDialog(defaultCategory = 'other') {
    return {
        open: false,
        mode: 'create',
        documentId: null,
        fileName: '',
        uploadedFileName: '',
        fileCreatedAt: '',
        file: null,
        documentCategory: defaultCategory,
        documentNumber: '',
        documentDate: '',
        description: '',
    };
}

export default {
    name: 'finance-documents',
    components: {
        AppDialog,
        AppDialogAction,
        CategoryCreateInput,
        ConfirmDialog,
    },
    metaInfo: {title: 'Finanzen · Dokumente'},
    data() {
        return {
            loading: false,
            uploading: false,
            filterSidebarOpen: false,
            categoryMenuOpen: false,
            labelManagerOpen: false,
            openDocumentLabelMenu: null,
            openDocumentActionMenu: null,
            deleteDocumentDialogOpen: false,
            pendingDeleteDocument: null,
            previewModal: {open: false, url: '', title: ''},
            viewMode: 'list',
            token: getAuthToken(),
            documents: [],
            documentCategories: [],
            labels: [],
            selectedLabelIds: [],
            canManageLabels: false,
            labelDraft: {name: '', color: UI_COLORS.text.subtle},
            filters: {search: '', documentCategory: '', linkState: 'all'},
            sort: {column: 'updated_at', order: 'desc'},
            page: 1,
            perPage: 25,
            pagination: {currentPage: 1, perPage: 25, total: 0, totalPages: 1},
            uploadDialog: emptyUploadDialog(),
        };
    },
    computed: {
        acceptedExtensions() {
            return '.pdf,.xml,.jpg,.jpeg,.png,.tif,.tiff,.docx,.xlsx,.csv';
        },
        hasActiveFilters() {
            return this.filters.search.trim() !== ''
                || this.filters.documentCategory !== ''
                || this.filters.linkState !== 'all'
                || this.selectedLabelIds.length > 0
                || this.sort.column !== 'updated_at'
                || this.sort.order !== 'desc';
        },
        categoryOptions() {
            return [
                {value: '', label: 'Alle Kategorien'},
                ...this.documentCategories,
            ];
        },
        currentCategoryLabel() {
            const selected = this.categoryOptions.find((category) => category.value === this.filters.documentCategory);
            return selected ? selected.label : 'Alle Kategorien';
        },
        currentUser() {
            return this.$store && this.$store.state ? this.$store.state.user : null;
        },
        accessState() {
            return resolveAccessState(this.$store);
        },
        selectedDialogFileLabel() {
            if (this.uploadDialog.file) return this.uploadDialog.file.name;
            return 'Keine Datei ausgewählt';
        },
        editFileUploadedAtDate() {
            return this.formatDateTimePart(this.uploadDialog.fileCreatedAt, 'date');
        },
        editFileUploadedAtTime() {
            return this.formatDateTimePart(this.uploadDialog.fileCreatedAt, 'time');
        },
        dialogSubmitDisabled() {
            return this.uploading
                || (this.uploadDialog.mode === 'create' && !this.uploadDialog.file)
                || (this.uploadDialog.mode === 'edit' && !this.uploadDialog.fileName.trim());
        },
        paginationStart() {
            return this.pagination.total > 0 ? ((this.pagination.currentPage - 1) * this.pagination.perPage) + 1 : 0;
        },
        paginationEnd() {
            return Math.min(this.pagination.currentPage * this.pagination.perPage, this.pagination.total);
        },
    },
    mounted() {
        this.fetchOptions();
        this.fetchDocuments();
    },
    methods: {
        closeCategoryMenu() {
            this.categoryMenuOpen = false;
        },
        selectCategory(value) {
            this.filters.documentCategory = value;
            this.closeCategoryMenu();
            this.applyFilters();
        },
        documentLabelMenuKey(document) {
            return document ? Number(document.id) : null;
        },
        isDocumentLabelMenuOpen(document) {
            return this.openDocumentLabelMenu === this.documentLabelMenuKey(document);
        },
        toggleDocumentLabelMenu(document) {
            const key = this.documentLabelMenuKey(document);
            this.openDocumentLabelMenu = this.openDocumentLabelMenu === key ? null : key;
            this.closeDocumentActionMenu();
        },
        closeDocumentLabelMenu() {
            this.openDocumentLabelMenu = null;
        },
        assignLabel(document, label) {
            if (!document || !document.can_write) return;
            axios.patch(`api/workspace/finance-documents/${document.id}/label`, {
                label_id: label ? Number(label.id) : null,
            }).then((response) => {
                Object.assign(document, response.data.document || document);
                this.closeDocumentLabelMenu();
                this.fetchOptions();
                if (this.selectedLabelIds.length > 0) this.fetchDocuments();
            });
        },
        documentLabel(document) {
            return document && document.file ? document.file.label : null;
        },
        documentActionMenuKey(document) {
            return document ? Number(document.id) : null;
        },
        isDocumentActionMenuOpen(document) {
            return this.openDocumentActionMenu === this.documentActionMenuKey(document);
        },
        toggleDocumentActionMenu(document) {
            const key = this.documentActionMenuKey(document);
            this.openDocumentActionMenu = this.openDocumentActionMenu === key ? null : key;
            this.closeDocumentLabelMenu();
        },
        closeDocumentActionMenu() {
            this.openDocumentActionMenu = null;
        },
        openEditFromActionMenu(document) {
            this.closeDocumentActionMenu();
            this.openEditDialog(document);
        },
        openDeleteDocumentDialog(document) {
            if (!document || !document.can_delete) return;
            this.closeDocumentActionMenu();
            this.pendingDeleteDocument = document;
            this.deleteDocumentDialogOpen = true;
        },
        closeDeleteDocumentDialog() {
            this.deleteDocumentDialogOpen = false;
            this.pendingDeleteDocument = null;
        },
        deleteDocument() {
            const document = this.pendingDeleteDocument;
            if (!document || !document.can_delete) {
                this.closeDeleteDocumentDialog();
                return;
            }
            axios.delete(`api/workspace/finance-documents/${document.id}`).then(() => {
                this.closeDeleteDocumentDialog();
                this.fetchOptions();
                this.fetchDocuments();
                this.$notify({title: 'Erfolg', text: 'Finanzdokument wurde gelöscht.', type: 'success'});
            });
        },
        updaterUser(document) {
            return document && (document.updater || document.creator)
                ? (document.updater || document.creator)
                : null;
        },
        updaterAvatar(document) {
            return resolveFileUserAvatar(this.updaterUser(document));
        },
        updaterName(document) {
            const user = this.updaterUser(document);
            return user && user.name ? user.name : '-';
        },
        isImageDocument(document) {
            return isImageFileExtension(document && document.file ? document.file.extension : '');
        },
        openDocumentPreview(document) {
            if (!this.isImageDocument(document)) return;
            this.previewModal = {
                open: true,
                url: buildFilePreviewUrl(document.file, this.token),
                title: this.fileName(document),
            };
        },
        closeDocumentPreview() {
            this.previewModal = {open: false, url: '', title: ''};
        },
        toggleLabelManager() {
            this.labelManagerOpen = !this.labelManagerOpen;
            if (this.labelManagerOpen) {
                this.focusLabelManagerInput();
            }
        },
        closeLabelManager() {
            this.labelManagerOpen = false;
        },
        focusLabelManagerInput() {
            this.$nextTick(() => {
                const input = this.$refs.labelManagerInput;
                if (input && typeof input.focusInput === 'function') {
                    input.focusInput();
                } else if (input && typeof input.focus === 'function') {
                    input.focus();
                }
            });
        },
        canDeleteLabel(label) {
            if (!label || !this.currentUser) return false;
            return this.accessState.isAdmin
                || Number(label.created_by_user_id || 0) === Number(this.currentUser.id || 0);
        },
        createLabel() {
            const name = String(this.labelDraft.name || '').trim();
            if (!name) {
                this.$notify({
                    title: 'Fehler',
                    text: 'Bitte eine Farbe und einen Labeltext auswählen.',
                    type: 'error',
                });
                this.focusLabelManagerInput();
                return;
            }
            axios.post('api/workspace/finance-documents/labels', {
                name,
                color: this.labelDraft.color || UI_COLORS.text.subtle,
            }).then((response) => {
                this.labels = response.data.labels || this.labels;
                this.labelDraft = {name: '', color: UI_COLORS.text.subtle};
                this.fetchDocuments();
                this.focusLabelManagerInput();
            });
        },
        deleteLabel(label) {
            if (!this.canDeleteLabel(label) || !window.confirm(`Soll das Label „${label.name}“ gelöscht werden?`)) return;
            axios.delete(`api/workspace/finance-documents/labels/${label.id}`).then((response) => {
                this.labels = response.data.labels || [];
                this.selectedLabelIds = this.selectedLabelIds.filter((id) => Number(id) !== Number(label.id));
                this.fetchDocuments();
            });
        },
        fetchOptions() {
            axios.get('api/workspace/finance-documents/options').then((response) => {
                this.documentCategories = response.data.document_categories || [];
                this.labels = response.data.labels || [];
                this.canManageLabels = !!response.data.can_manage_labels;
            });
        },
        fetchDocuments() {
            this.loading = true;
            axios.get('api/workspace/finance-documents', {
                params: {
                    page: this.page,
                    perPage: this.perPage,
                    search: this.filters.search,
                    document_category: this.filters.documentCategory,
                    link_state: this.filters.linkState,
                    labels: this.selectedLabelIds,
                    sort: this.sort,
                },
            }).then((response) => {
                this.documents = response.data.items || [];
                this.pagination = response.data.pagination || this.pagination;
                if (Array.isArray(response.data.document_categories)) {
                    this.documentCategories = response.data.document_categories;
                }
                if (Array.isArray(response.data.labels)) {
                    this.labels = response.data.labels;
                }
            }).finally(() => {
                this.loading = false;
            });
        },
        applyFilters() {
            this.page = 1;
            this.fetchDocuments();
        },
        resetFilters() {
            this.filters = {search: '', documentCategory: '', linkState: 'all'};
            this.selectedLabelIds = [];
            this.sort = {column: 'updated_at', order: 'desc'};
            this.applyFilters();
        },
        toggleSort() {
            this.sort.order = this.sort.order === 'asc' ? 'desc' : 'asc';
            this.applyFilters();
        },
        changePage(page) {
            if (page > 0 && page <= this.pagination.totalPages && page !== this.page) {
                this.page = page;
                this.fetchDocuments();
            }
        },
        openUploadDialog() {
            this.uploadDialog = emptyUploadDialog(this.filters.documentCategory || 'other');
            this.uploadDialog.open = true;
        },
        openEditDialog(document) {
            if (!document || !document.can_write) return;
            this.uploadDialog = {
                open: true,
                mode: 'edit',
                documentId: Number(document.id),
                fileName: this.fileName(document),
                uploadedFileName: document.file && document.file.original_name ? document.file.original_name : this.fileName(document),
                fileCreatedAt: document.file && document.file.created_at ? document.file.created_at : document.created_at,
                file: null,
                documentCategory: document.document_category || 'other',
                documentNumber: document.document_number || '',
                documentDate: document.document_date || '',
                description: document.description || '',
            };
        },
        closeUploadDialog() {
            this.uploadDialog = emptyUploadDialog(this.filters.documentCategory || 'other');
            if (this.$refs.uploadInput) {
                this.$refs.uploadInput.value = null;
            }
        },
        selectUploadFile(event) {
            this.uploadDialog.file = event.target.files && event.target.files.length ? event.target.files[0] : null;
        },
        submitDocumentDialog() {
            if (this.uploadDialog.mode === 'edit') {
                this.updateDocument();
                return;
            }
            this.uploadDocument();
        },
        uploadDocument() {
            if (!this.uploadDialog.file || this.uploading) {
                return;
            }
            const formData = new FormData();
            formData.append('file', this.uploadDialog.file);
            formData.append('document_category', this.uploadDialog.documentCategory || 'other');
            if (this.uploadDialog.documentNumber) formData.append('document_number', this.uploadDialog.documentNumber);
            if (this.uploadDialog.documentDate) formData.append('document_date', this.uploadDialog.documentDate);
            if (this.uploadDialog.description) formData.append('description', this.uploadDialog.description);

            this.uploading = true;
            axios.post('api/workspace/finance-documents', formData, {headers: {'Content-Type': 'multipart/form-data'}})
                .then(() => {
                    this.closeUploadDialog();
                    this.fetchOptions();
                    this.fetchDocuments();
                    this.$notify({title: 'Erfolg', text: 'Dokument erfolgreich hochgeladen.', type: 'success'});
                })
                .finally(() => {
                    this.uploading = false;
                });
        },
        updateDocument() {
            if (!this.uploadDialog.documentId || this.uploading) return;
            const formData = new FormData();
            formData.append('_method', 'PATCH');
            formData.append('name', this.uploadDialog.fileName.trim());
            formData.append('document_category', this.uploadDialog.documentCategory || 'other');
            formData.append('document_number', this.uploadDialog.documentNumber || '');
            formData.append('document_date', this.uploadDialog.documentDate || '');
            formData.append('description', this.uploadDialog.description || '');
            if (this.uploadDialog.file) formData.append('file', this.uploadDialog.file);

            this.uploading = true;
            axios.post(`api/workspace/finance-documents/${this.uploadDialog.documentId}`, formData, {
                headers: {'Content-Type': 'multipart/form-data'},
            }).then(() => {
                this.closeUploadDialog();
                this.fetchDocuments();
                this.$notify({title: 'Erfolg', text: 'Finanzdokument wurde aktualisiert.', type: 'success'});
            }).finally(() => {
                this.uploading = false;
            });
        },
        fileName(document) {
            return document && document.file ? document.file.name : 'Dokument';
        },
        fileMeta(document) {
            const file = document && document.file ? document.file : {};
            return `${String(file.extension || '-').toUpperCase()}, ${file.size_readable || '-'}`;
        },
        tileMeta(document) {
            return `${this.fileMeta(document)} · ${this.linkLabel(document)}`;
        },
        shortDescriptionTooltip: buildShortDescriptionTooltip,
        fileIcon(document) {
            return resolveFileIcon(document && document.file ? document.file.extension : '');
        },
        linkLabel(document) {
            const count = Number(document.finance_entries_count || 0);
            if (count === 0) return 'Keine Verknüpfungen';
            if (count === 1) return '1 Finanzeintrag';
            return `${count} Finanzeinträge`;
        },
        downloadUrl(document) {
            const file = document && document.file ? document.file : {};
            return `${file.download || '#'}?token=${this.token}`;
        },
        formatDate(value) {
            if (!value) return '-';
            return new Intl.DateTimeFormat('de-DE').format(new Date(`${value}T00:00:00`));
        },
        formatDateTimePart(value, part) {
            if (!value) return part === 'date' ? '--.--.----' : '--:--';
            const date = new Date(value);
            if (Number.isNaN(date.getTime())) return part === 'date' ? '--.--.----' : '--:--';
            if (part === 'time') {
                return new Intl.DateTimeFormat('de-DE', {hour: '2-digit', minute: '2-digit'}).format(date);
            }
            return new Intl.DateTimeFormat('de-DE', {day: '2-digit', month: '2-digit', year: 'numeric'}).format(date);
        },
    },
};
</script>

<style lang="scss">
@reference "../../../../../sass/tailwind-reference.css";

.finance-document-category-select {
    min-width: 18rem;
}

.finance-document-view-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1.75rem;
    height: 1.75rem;
    border: 0;
    border-radius: 0.25rem;
    background: transparent;
    color: var(--ui-text-default);
    cursor: pointer;
}

.finance-document-view-btn--active {
    color: var(--ui-text-subtle);
    cursor: default;
}

.finance-document-view-btn:focus,
.finance-document-view-btn:focus-visible,
.finance-document-view-btn:active {
    outline: none;
    box-shadow: none;
}

.finance-document-category-trigger {
    width: 100%;
}

.finance-document-category-trigger:focus,
.finance-document-category-trigger:focus-visible,
.finance-document-category-trigger:active {
    outline: none;
    box-shadow: none;
}

.finance-document-category-menu {
    z-index: 120;
}

.finance-document-label-dot {
    display: inline-flex;
    width: 0.625rem;
    min-width: 0.625rem;
    height: 0.625rem;
    border-radius: 9999px;
    flex-shrink: 0;
}

.finance-document-label-menu,
.finance-document-label-manager,
.finance-document-action-menu {
    z-index: 140;
}

.finance-document-label-manager-row {
    padding: 0.5rem 0;
}

.finance-document-label-manager-row-content {
    padding-left: 0.7rem;
}

.finance-document-label-manager-row-dot {
    width: 1rem;
    height: 1rem;
    margin-right: 0.5rem;
}

.finance-document-label-manager-row-actions {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.35rem;
    min-width: 2.35rem;
}

.finance-document-label-delete-btn {
    width: 2.35rem;
    height: 2rem;
    opacity: 0;
    pointer-events: none;
    transition: opacity 120ms ease-in-out;
}

.finance-document-label-manager-row:hover .finance-document-label-delete-btn {
    opacity: 1;
    pointer-events: auto;
}

.finance-document-card {
    position: relative;
    z-index: 1;
    overflow: visible;
}

.finance-document-card:hover,
.finance-document-card:focus-within {
    z-index: 600;
}

.finance-document-file-icon {
    color: var(--ui-text-subtle);
}

.finance-document-tile-header {
    display: flex;
    height: 11.5rem;
    flex-direction: column;
    align-items: center;
    justify-content: center;
}

.finance-document-title-text {
    display: block;
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.finance-document-list-sticky-th {
    position: sticky;
    top: 0;
    z-index: 10;
    padding-top: 0.875rem;
    padding-bottom: 0.875rem;
    background-color: var(--ui-surface-raised);
    white-space: nowrap;
}

.finance-document-list-table {
    min-width: 100%;
    border-bottom: 1px solid var(--border-ultralight);
    table-layout: auto;
}

.finance-document-field-label {
    display: block;
    margin-bottom: 0.5rem;
    color: var(--ui-text-muted);
    font-size: 0.75rem;
    font-weight: 500;
}

@media (min-width: 1400px) {
    .finance-document-tile-grid {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

@media (min-width: 1800px) {
    .finance-document-tile-grid {
        grid-template-columns: repeat(5, minmax(0, 1fr));
    }
}
</style>
